import React, { useEffect, useMemo, useState } from "react";
import Select from "@8x8/oxygen-select";
import Modal, { ModalHeader } from "@8x8/oxygen-modal";
import { FreeformAddressSelector } from "@/react/features/support-tools/siptrunk/dids/FreeformAddressSelector";
import { ADDRESS_MODAL_ACTIONS } from "@/react/features/support-tools/siptrunk/constants";
import { Button } from "@/react/styled";
import styled from "styled-components";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";
import Tooltip from "@8x8/oxygen-tooltip";
import { IconButton } from "@8x8/oxygen-icon-button";
import { PencilIcon } from "@8x8/oxygen-icon";
import useEditableCell from "@/react/components/table/useEditableCell";

const OptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoPaddingButton = styled(Button)`
  padding: 0;
  min-height: auto;
`;

export const AddressCell = (props) => {
  const { value, onChange } = useEditableCell(props);

  /**
   * Only unique addresses will be displayed as options
   */
  const addressOptions = useMemo(() => {
    return props?.table.getRowModel().rows.reduce((acc, row) => {
      const addressId = row.original?.address?.addressId;
      if (!acc.some((option) => option.value === addressId)) {
        acc.push({
          label: row.original?.address?.freeForm,
          value: addressId,
        });
      }
      return acc;
    }, []);
  }, [props]);

  const [addressModalOpenFor, setAddressModalOpenFor] = useState(null);
  const [shouldOpenEditModal, setShouldOpenEditModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState({
    label: value?.freeForm,
    value: value?.addressId,
  });

  /**
   * useEffect required to ensure that the selectedAddress is set before
   * changing the addressModalOpenFor, to ensure predictability on data
   */
  useEffect(() => {
    if (shouldOpenEditModal && selectedAddress) {
      setAddressModalOpenFor(ADDRESS_MODAL_ACTIONS.EDIT);
      setShouldOpenEditModal(false);
    }
  }, [shouldOpenEditModal, selectedAddress]);

  const handleEditClick = (e, option) => {
    e.stopPropagation();
    setSelectedAddress(option);
    setShouldOpenEditModal(true);
  };

  const handleAddNewClick = () => {
    setAddressModalOpenFor(ADDRESS_MODAL_ACTIONS.CREATE);
    setSelectedAddress(null);
  };

  const saveAddress = (address) => {
    onChange({
      freeForm: address.freeForm,
      addressId: address.id,
    });
    setAddressModalOpenFor(null);
  };

  const customOptions = useMemo(
    () => [
      ...(addressOptions || []).map((option) => ({
        ...option,
        label: (
          <OptionContainer>
            <IconButton onClick={(e) => handleEditClick(e, option)}>
              <PencilIcon />
            </IconButton>
            <Tooltip title={option.label}>{option.label}</Tooltip>
          </OptionContainer>
        ),
      })),
      {
        value: "add_new",
        label: (
          <NoPaddingButton variant="text" onClick={handleAddNewClick}>
            + Add New
          </NoPaddingButton>
        ),
      },
    ],
    [addressOptions]
  );

  return (
    <FullWidthDiv>
      {addressModalOpenFor && (
        <Modal
          onClose={() => setAddressModalOpenFor(null)}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
        >
          <ModalHeader
            title={"Address Validation"}
            onClose={() => setAddressModalOpenFor(null)}
          />
          <FreeformAddressSelector
            selectedAddress={{ id: selectedAddress?.value }}
            action={addressModalOpenFor}
            onSave={saveAddress}
          />
        </Modal>
      )}
      <Select
        size={"small"}
        options={customOptions}
        onChange={(opt) => setSelectedAddress(opt)}
        value={selectedAddress}
        placeholder="Select an option"
      />
    </FullWidthDiv>
  );
};
